import { useEffect, useState } from "react";
import {
  Box,
  TableCell,
  TableRow,
  IconButton,
  Icon,
  Tooltip,
  Chip,
} from "@mui/material";
import { FerramanetasDaListagem } from "../../shared/components/ferramentasDaListagem/ferramentas-listagem";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { ENVIRONMENT } from "../../environment/environment";
import { useNavigate } from "react-router-dom";
import { PaginacaoListagem } from "../../shared/components/ferramentasDaListagem/paginacao-listagem";
import { useDebounce } from "../../shared/utils/useDebounce/use-debounce";
import { toastError } from "../../shared/services/errors/erros-handler";
import { cadastroBancarioService } from "./service/cadastro-bancario.service";
import { TabelaDaListagem } from "../../shared/components/tabela-da-listagem/tabelaDalistagem";
import { BankAccountType } from "../../shared/utils/enum/enum";
import { toast } from "react-toastify";

export interface AccountListItem {
  id: string;
  name: string;
  accountNumber: string;
  digit: string;
  type: BankAccountType;
  initialBalance?: number;
  active: boolean;
  agency?: {
    id: string;
    name: string;
    agencyNumber: string;
    digit?: string;
  };
  bank?: {
    id: string;
    name: string;
  };
}

export function ListagemCadastroBancario() {
  const [contas, setContas] = useState<AccountListItem[]>([]);
  const [busca, setBusca] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const buscaDebounce = useDebounce(busca, 500);
  const navigate = useNavigate();

  const carregarContas = async (
    filtro?: string,
    pagina = page,
    limite = rowsPerPage
  ) => {
    try {
      setIsLoading(true);
      const result = await cadastroBancarioService.findAll(
        filtro,
        pagina,
        limite
      );
      setContas(result.items || []);
      setTotalCount(result.totalItems || 0);
    } catch (error) {
      setContas([]);
      toastError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    carregarContas(undefined, 0);
  }, []);

  useEffect(() => {
    setPage(0);
    carregarContas(buscaDebounce, 0);
  }, [buscaDebounce]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    carregarContas(buscaDebounce, newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    carregarContas(buscaDebounce, 0, newLimit);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Tem certeza que deseja excluir este banco?")) {
      try {
        const obraDeletada = await cadastroBancarioService.deletar(id);
        toast.success(obraDeletada);
        carregarContas(buscaDebounce, page, rowsPerPage);
      } catch (error) {
        toastError(error);
      }
    }
  };

  return (
    <BaseLayout
      title="Listagem de Contas Bancárias"
      barraDeFerramentas={
        <FerramanetasDaListagem
          placeholder={ENVIRONMENT.INPUT_DE_BUSCA + " por nome"}
          textoBotaoNovo="Adicionar Nova Conta"
          mostrarInputBusca
          textoDaBusca={busca}
          aoMudarTextoDeBusca={setBusca}
          aoClicarBotaoNovo={() =>
            navigate("/empresas/cadastro-bancario/criar")
          }
        />
      }
    >
      <Box display="flex" flexDirection="column" paddingBottom="40px">
        <PaginacaoListagem
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />

        <TabelaDaListagem
          ariaLabel="tabela de contas bancárias"
          headTable={[
            "Nome",
            "Conta",
            "Tipo",
            "Saldo Inicial",
            "Situação",
            "Agência",
            "Banco",
            "Ações",
          ]}
          columnWidths={[480, 120, 120, 120, 120, 200, 200, 150]}
          tableBody={
            !isLoading && Array.isArray(contas) && contas.length > 0 ? (
              contas.map((conta) => (
                <TableRow hover key={conta.id}>
                  <TableCell>{conta.name}</TableCell>
                  <TableCell>{conta.accountNumber + "-" + conta.digit}</TableCell>
                  <TableCell>
                    {conta.type === BankAccountType.CHECKING_ACCOUNT
                      ? "Conta Corrente"
                      : "Conta Poupança"}
                  </TableCell>
                  <TableCell>
                    {conta.initialBalance !== undefined &&
                    conta.initialBalance !== null
                      ? Number(conta.initialBalance).toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })
                      : "R$ 0,00"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={conta.active ? "Ativa" : "Inativa"}
                      color={conta.active ? "success" : "default"}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{conta.agency?.name || "-"}</TableCell>
                  <TableCell>{conta.bank?.name || "-"}</TableCell>
                  <TableCell>
                    <Tooltip title="Editar Cadastro Bancário">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(
                            `/empresas/cadastro-bancario/detalhe/${conta.bank?.id}`
                          )
                        }
                      >
                        <Icon>edit</Icon>
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir Cadastro Bancário">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(conta.bank?.id || "")}
                      >
                        <Icon>delete</Icon>
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  {isLoading ? "Carregando..." : "Nenhuma conta encontrada :("}
                </TableCell>
              </TableRow>
            )
          }
        />
      </Box>
    </BaseLayout>
  );
}
